import React, { useEffect, useState, useMemo } from "react";
import AppHeader from "@/components/AppHeader";
import BottomNavigation from "@/components/BottomNavigation";
import { Building2, ShoppingCart, Phone, MapPin, Globe, Search, Filter, X, ChevronUp, ChevronDown, Check, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { StatusBadge, MonoValue } from "@/components/redesign";

// Types for the nearby companies
interface Company {
  id: string;
  name: string;
  category: string;
  description: string;
  address: string;
  city: string;
  country: string;
  phone?: string;
  website?: string;
  latitude: number;
  longitude: number;
  ad: boolean;
  ad_start_date?: string;
  ad_end_date?: string;
}

// Category key mapping for translations
const CATEGORY_KEYS: Record<string, string> = {
  "Usta": "usta",
  "Muhendislik Hizmetleri": "muhendislik_hizmetleri",
  "Beton": "beton",
  "Insaat Malzemesi": "insaat_malzemesi",
  "Pen Isleri": "pen_isleri",
  "Seramik": "seramik",
  "Vitrifiye": "vitrifiye",
  "Tesisat Malzemesi": "tesisat_malzemesi",
  "Elektrik Malzemesi": "elektrik_malzemesi",
  "Ankastre - Beyaz Esya": "ankastre_beyaz_esya",
  "Iklimlendirme - Klima": "iklimlendirme_klima",
  "Dogalgaz": "dogalgaz",
  "Peyzaj - Cevre Duzenlemesi": "peyzaj_cevre_duzenlemesi",
  "Hafriyat": "hafriyat",
};


// Category emoji mapping
const categoryEmojis: Record<string, string> = {
  "usta": "🧰",
  "muhendislik_hizmetleri": "📐",
  "beton": "🏗️",
  "insaat_malzemesi": "📦",
  "pen_isleri": "🪟",
  "seramik": "🧱",
  "vitrifiye": "🚿",
  "tesisat_malzemesi": "🔧",
  "elektrik_malzemesi": "💡",
  "ankastre_beyaz_esya": "🍽️",
  "iklimlendirme_klima": "❄️",
  "dogalgaz": "🔥",
  "peyzaj_cevre_duzenlemesi": "🌳",
  "hafriyat": "🚜",
};

// Helper functions for category translation and styling
const getCategoryDisplayName = (category: string, t: any) => {
  const categoryKey = CATEGORY_KEYS[category];
  if (categoryKey) {
    return t(`marketplace.business_categories.${categoryKey}`);
  }
  return category; // Fallback to original if not found
};

// Get category key for color/emoji lookup
const getCategoryKey = (category: string) => {
  return CATEGORY_KEYS[category] || category.toLowerCase().replace(/\s+/g, '_');
};

// Company card component to avoid repetition
const CompanyCard: React.FC<{ company: Company }> = ({ company }) => {
  const { t } = useTranslation();
  
  const handleCall = (phone: string) => {
    // Fix phone call functionality by using proper link format
    const phoneNumber = phone.replace(/\s+/g, ''); // Remove spaces
    
    try {
      
      // Create and click an actual anchor element instead of window.open
      const a = document.createElement('a');
      a.href = `tel:${phoneNumber}`;
      a.setAttribute('target', '_system'); // Use _system target
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
    } catch (error) {
      
    }
  };

  const handleGetDirections = (lat: number, lng: number) => {
    const fullAddress = `${company.address}, ${company.city}, ${company.country}`;
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
    window.open(mapsUrl, '_blank');
  };


  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 space-y-3">
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center text-lg">
            {categoryEmojis[getCategoryKey(company.category)] || '🏢'}
          </div>
          <div className="min-w-0">
            <p className="text-[15px] font-bold text-gray-900 truncate">{company.name}</p>
            <StatusBadge tone="brand">
              {getCategoryDisplayName(company.category, t)}
            </StatusBadge>
          </div>
        </div>
      </div>

      {/* Description */}
      <p className="text-[13px] text-gray-600 line-clamp-2">{company.description}</p>

      {/* Contact info */}
      <div className="space-y-1">
        <div className="flex items-center gap-1.5 text-gray-500">
          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="text-[12px] truncate">{company.address}, {company.city}, {company.country}</span>
        </div>
        {company.phone && (
          <div className="flex items-center gap-1.5 text-gray-500">
            <Phone className="h-3.5 w-3.5 flex-shrink-0" />
            <MonoValue value={company.phone} size="sm" tone="muted" />
          </div>
        )}
        {company.website && (
          <div className="flex items-center gap-1.5 text-gray-500">
            <Globe className="h-3.5 w-3.5 flex-shrink-0" />
            <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-[12px] text-brand-600 truncate max-w-[160px] hover:underline">
              {company.website.replace(/^https?:\/\//, '')}
            </a>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 pt-1">
        {company.phone && (
          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-8 text-xs border-gray-200 text-gray-700"
            onClick={() => handleCall(company.phone!)}
          >
            <Phone className="mr-1.5 h-3 w-3" /> {t('marketplace.call')}
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          className="flex-1 h-8 text-xs border-brand-200 text-brand-600 bg-brand-50 hover:bg-brand-100"
          onClick={() => handleGetDirections(company.latitude, company.longitude)}
        >
          <MapPin className="mr-1.5 h-3 w-3" /> {t('marketplace.directions')}
        </Button>
      </div>
    </div>
  );
};

interface FilterOptions {
  searchQuery: string;
  selectedCategories: string[];
  selectedCountries: string[];
  selectedCities: string[];
}

// Multi-select component
interface MultiSelectProps {
  options: string[];
  selected: string[];
  onSelectionChange: (selected: string[]) => void;
  placeholder: string;
  emptyText: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selected,
  onSelectionChange,
  placeholder,
  emptyText
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const handleSelect = (value: string) => {
    const newSelected = selected.includes(value)
      ? selected.filter(item => item !== value)
      : [...selected, value];
    onSelectionChange(newSelected);
  };

  const removeSelected = (value: string) => {
    onSelectionChange(selected.filter(item => item !== value));
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between h-auto min-h-[2.5rem] p-2"
          >
            <div className="flex flex-wrap gap-1">
              {selected.length === 0 ? (
                <span className="text-gray-500">{placeholder}</span>
              ) : (
                selected.map((item) => (
                  <Badge
                    key={item}
                    variant="secondary"
                    className="mr-1 mb-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSelected(item);
                    }}
                  >
                    {item}
                    <X className="ml-1 h-3 w-3" />
                  </Badge>
                ))
              )}
            </div>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder={t('common.search')} />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option}
                    value={option}
                    onSelect={() => handleSelect(option)}
                  >
                    <Checkbox
                      checked={selected.includes(option)}
                      className="mr-2"
                    />
                    {option}
                    {selected.includes(option) && (
                      <Check className="ml-auto h-4 w-4" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const YakindakiIsletmelerTab: React.FC<{ navigate: (path: string) => void }> = ({ navigate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [businessData, setBusinessData] = useState<{
    ads: Company[];
    grouped: Record<string, Company[]>;
  }>({
    ads: [],
    grouped: {},
  });
  const [originalData, setOriginalData] = useState<{
    ads: Company[];
    grouped: Record<string, Company[]>;
  }>({
    ads: [],
    grouped: {},
  });
  
  // Filter options - Updated to support arrays
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    searchQuery: "",
    selectedCategories: [],
    selectedCountries: [],
    selectedCities: []
  });
  
  // For the location filter
  const [countries, setCountries] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);

  // State for collapsible filter
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    // Fetch all companies on initial load
    fetchCompanies();
    // Fetch unique countries, cities, and categories for filters
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    // Apply filters when filter options change
    applyFilters();
  }, [filterOptions, originalData]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('nearby_companies')
        .select('*');
      
      if (error) throw error;

      // Sort companies - first by ads, then alphabetically
      const today = new Date();
      const processedCompanies = (data || []).sort((a, b) => {
        // Check if company has an active ad
        const aHasActiveAd = a.ad && a.ad_start_date && a.ad_end_date && 
          isDateInRange(today, new Date(a.ad_start_date), new Date(a.ad_end_date));
        const bHasActiveAd = b.ad && b.ad_start_date && b.ad_end_date && 
          isDateInRange(today, new Date(b.ad_start_date), new Date(b.ad_end_date));
        
        // Companies with active ads come first
        if (aHasActiveAd && !bHasActiveAd) return -1;
        if (!aHasActiveAd && bHasActiveAd) return 1;
        
        // If both have active ads or both don't, sort alphabetically
        return a.name.localeCompare(b.name);
      });

      // Restructure the companies into ads and grouped by category
      const ads: Company[] = [];
      const grouped: Record<string, Company[]> = {};

      processedCompanies.forEach(company => {
        const hasActiveAd = company.ad && company.ad_start_date && company.ad_end_date &&
          isDateInRange(today, new Date(company.ad_start_date), new Date(company.ad_end_date));
        
        if (hasActiveAd) {
          ads.push(company);
        } else {
          if (!grouped[company.category]) {
            grouped[company.category] = [];
          }
          grouped[company.category].push(company);
        }
      });

      setBusinessData({ ads, grouped });
      setOriginalData({ ads, grouped });
    } catch (err) {
      setError(t('marketplace.loading_businesses'));
      
    } finally {
      setLoading(false);
    }
  };
  
  const fetchFilterOptions = async () => {
    try {
      const { data, error } = await supabase
        .from('nearby_companies')
        .select('country, city, category');
      
      if (error) throw error;
      
      // Extract unique countries, cities, and categories
      const uniqueCountries = Array.from(new Set(
        (data || []).map(item => item.country).filter(Boolean)
      )).sort();
      
      const uniqueCities = Array.from(new Set(
        (data || []).map(item => item.city).filter(Boolean)
      )).sort();

      const uniqueCategories = Array.from(new Set(
        (data || []).map(item => item.category).filter(Boolean)
      )).sort();
      
      setCountries(uniqueCountries);
      setCities(uniqueCities);
      setCategories(uniqueCategories);
    } catch (err) {
      
    }
  };

  const isDateInRange = (date: Date, start: Date, end: Date): boolean => {
    return date >= start && date <= end;
  };
  
  const applyFilters = () => {
    
    if (originalData.ads.length === 0 && Object.keys(originalData.grouped).length === 0) return;

    const normalizedQuery = filterOptions.searchQuery.toLowerCase().trim();
    
    // Get all companies
    const allCompanies = [
      ...originalData.ads,
      ...Object.values(originalData.grouped).flat()
    ];
    
    const filteredCompanies = allCompanies.filter(company => {
      // Filter by name/description
      const nameMatch = !normalizedQuery || 
        company.name.toLowerCase().includes(normalizedQuery) ||
        company.description.toLowerCase().includes(normalizedQuery);
      
      // Filter by categories
      const categoryMatch = filterOptions.selectedCategories.length === 0 ||
        filterOptions.selectedCategories.includes(company.category);
      
      // Filter by countries
      const countryMatch = filterOptions.selectedCountries.length === 0 ||
        filterOptions.selectedCountries.includes(company.country);
      
      // Filter by cities
      const cityMatch = filterOptions.selectedCities.length === 0 ||
        filterOptions.selectedCities.includes(company.city);
      
      return nameMatch && categoryMatch && countryMatch && cityMatch;
    });
    
    // Separate into ads and non-ads
    const today = new Date();
    const filteredAds: Company[] = [];
    const filteredGrouped: Record<string, Company[]> = {};
    
    filteredCompanies.forEach(company => {
      const hasActiveAd = company.ad && company.ad_start_date && company.ad_end_date &&
        isDateInRange(today, new Date(company.ad_start_date), new Date(company.ad_end_date));
      
      if (hasActiveAd) {
        filteredAds.push(company);
      } else {
        if (!filteredGrouped[company.category]) {
          filteredGrouped[company.category] = [];
        }
        filteredGrouped[company.category].push(company);
      }
    });
    
    setBusinessData({
      ads: filteredAds,
      grouped: filteredGrouped
    });
  };
  
  const handleFilterChange = (field: keyof FilterOptions, value: any) => {
    
    setFilterOptions(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const resetFilters = () => {
    setFilterOptions({
      searchQuery: "",
      selectedCategories: [],
      selectedCountries: [],
      selectedCities: []
    });
  };

  const hasActiveFilters = filterOptions.searchQuery || 
    filterOptions.selectedCategories.length > 0 || 
    filterOptions.selectedCountries.length > 0 || 
    filterOptions.selectedCities.length > 0;

  const totalSelectedFilters = filterOptions.selectedCategories.length + 
    filterOptions.selectedCountries.length + 
    filterOptions.selectedCities.length;

  const hasResults = businessData.ads.length > 0 || Object.keys(businessData.grouped).length > 0;

  return (
    <div className="flex flex-col h-full relative">
      {/* Backdrop overlay when filter is open */}
      {isFilterOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-20"
          onClick={() => setIsFilterOpen(false)}
        />
      )}
      
      <div className="sticky z-30 bg-white shadow-sm border-b border-gray-200" style={{
        top: 'calc(4rem + var(--safe-area-top, 0px))'
      }}>
        {/* Information Banner */}
        <div className="bg-brand-50 p-3 text-center text-brand-700 text-[12px] font-medium border-b border-brand-100">
          {t('marketplace.free_listing_info')}
        </div>

        {/* Add Company Button */}
        <div className="p-3 border-b border-gray-100 bg-white">
          <Button
            onClick={() => navigate("/add-company")}
            className="w-full bg-brand-600 hover:bg-brand-700 text-white font-semibold"
            size="sm"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('marketplace.add_company')}
          </Button>
        </div>
        
        <Collapsible 
          open={isFilterOpen} 
          onOpenChange={setIsFilterOpen}
        >
          <div className="p-3 border-b border-gray-100 bg-white">
            <div className="flex items-center justify-between gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t('marketplace.search_business')}
                  value={filterOptions.searchQuery}
                  onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
                  className="pl-9 pr-4 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-md"
                />
                {filterOptions.searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1 h-8 w-8 p-0"
                    onClick={() => handleFilterChange('searchQuery', '')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <CollapsibleTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1 whitespace-nowrap border-gray-200 text-gray-700 hover:bg-gray-50"
                >
                  <Filter className="h-4 w-4" />
                  {t('marketplace.filter')}
                  {totalSelectedFilters > 0 && (
                    <span className="ml-1 h-5 w-5 rounded-full bg-brand-600 text-white text-[10px] flex items-center justify-center font-bold">
                      {totalSelectedFilters}
                    </span>
                  )}
                  {isFilterOpen ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
            </div>
            
            {/* Show active filters as badges */}
            {hasActiveFilters && (
              <div className="mt-3 flex flex-wrap gap-2">
                              {filterOptions.selectedCategories.map(category => (
                <Badge key={`cat-${category}`} variant="outline" className="text-xs">
                  {categoryEmojis[getCategoryKey(category)] || '🏢'} {getCategoryDisplayName(category, t)}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-1 h-3 w-3 p-0"
                      onClick={() => handleFilterChange('selectedCategories', 
                        filterOptions.selectedCategories.filter(c => c !== category)
                      )}
                    >
                      <X className="h-2 w-2" />
                    </Button>
                  </Badge>
                ))}
                {filterOptions.selectedCountries.map(country => (
                  <Badge key={`country-${country}`} variant="outline" className="text-xs">
                    🌍 {country}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-1 h-3 w-3 p-0"
                      onClick={() => handleFilterChange('selectedCountries', 
                        filterOptions.selectedCountries.filter(c => c !== country)
                      )}
                    >
                      <X className="h-2 w-2" />
                    </Button>
                  </Badge>
                ))}
                {filterOptions.selectedCities.map(city => (
                  <Badge key={`city-${city}`} variant="outline" className="text-xs">
                    🏙️ {city}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-1 h-3 w-3 p-0"
                      onClick={() => handleFilterChange('selectedCities', 
                        filterOptions.selectedCities.filter(c => c !== city)
                      )}
                    >
                      <X className="h-2 w-2" />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}
            
            <CollapsibleContent className="mt-4">
              <div className="border border-gray-200 rounded-xl p-5 bg-white shadow-md relative z-40 space-y-5 mt-2">
                {/* Category Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('marketplace.categories')}</label>
                  <MultiSelect
                    options={categories}
                    selected={filterOptions.selectedCategories}
                    onSelectionChange={(selected) => handleFilterChange('selectedCategories', selected)}
                    placeholder={t('marketplace.category_select')}
                    emptyText={t('marketplace.category_not_found')}
                  />
                </div>
                
                {/* Country Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('marketplace.countries')}</label>
                  <MultiSelect
                    options={countries}
                    selected={filterOptions.selectedCountries}
                    onSelectionChange={(selected) => handleFilterChange('selectedCountries', selected)}
                    placeholder={t('marketplace.country_select')}
                    emptyText={t('marketplace.country_not_found')}
                  />
                </div>
                
                {/* City Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('marketplace.cities')}</label>
                  <MultiSelect
                    options={cities}
                    selected={filterOptions.selectedCities}
                    onSelectionChange={(selected) => handleFilterChange('selectedCities', selected)}
                    placeholder={t('marketplace.city_select')}
                    emptyText={t('marketplace.city_not_found')}
                  />
                </div>
                
                <div className="flex justify-center pt-2 border-t">
                  <Button onClick={resetFilters} variant="outline" className="w-full">
                    {t('marketplace.clear_all_filters')}
                  </Button>
                </div>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      </div>

      <div className="flex-1 overflow-y-auto relative z-10">
        {error && <div className="bg-error-50 border border-error-100 p-4 rounded-xl text-error-600 m-4 text-[13px]">{error}</div>}

        {loading ? (
          <div className="flex justify-center items-center min-h-[200px] p-4">
            <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-brand-600 border-t-transparent"></div>
          </div>
        ) : !hasResults ? (
          <div className="flex flex-col justify-center items-center h-full p-8 text-center">
            <div className="bg-brand-50 rounded-full p-5 inline-block mb-4">
              <Building2 className="h-10 w-10 text-brand-400" />
            </div>
            <h2 className="text-[18px] font-bold text-gray-900 mb-2">{t('marketplace.no_results')}</h2>
            <p className="text-[13px] text-gray-500 mb-4">
              {t('marketplace.search_criteria_not_found')}
            </p>
            {hasActiveFilters && (
              <Button onClick={resetFilters} variant="outline" className="border-gray-200 text-gray-700">
                {t('marketplace.clear_filters')}
              </Button>
            )}
          </div>
        ) : (
          <div className="p-4 space-y-8 pb-24">
            {/* Results summary */}
            <p className="text-[12px] text-gray-500">
              {businessData.ads.length + Object.values(businessData.grouped).flat().length} {t('marketplace.business_found')}
              {hasActiveFilters && ` ${t('marketplace.filtered')}`}
            </p>

            {/* Sponsored Businesses Section */}
            {businessData.ads.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
                  <span className="text-base">⭐</span>
                  <h2 className="text-[15px] font-bold text-gray-900">{t('marketplace.sponsored_businesses')}</h2>
                  <span className="text-[12px] text-gray-500">({businessData.ads.length})</span>
                </div>
                <div className="space-y-3">
                  {businessData.ads.map(company => (
                    <CompanyCard key={company.id} company={company} />
                  ))}
                </div>
              </div>
            )}

            {/* Categorized Businesses Sections */}
            {Object.entries(businessData.grouped).map(([category, companies]) => (
              <div key={category} className="space-y-3">
                <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
                  <span className="text-base">{categoryEmojis[getCategoryKey(category)] || '🏢'}</span>
                  <h2 className="text-[15px] font-bold text-gray-900">{getCategoryDisplayName(category, t)}</h2>
                  <span className="text-[12px] text-gray-500">({companies.length})</span>
                </div>
                <div className="space-y-3">
                  {companies.map(company => (
                    <CompanyCard key={company.id} company={company} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const MalzemeBorsasiTab: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [materialData, setMaterialData] = useState<Record<string, any[]>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Hepsi");

  useEffect(() => {
    fetchMaterialExchangeData();
  }, []);

  const fetchMaterialExchangeData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('material_exchange')
        .select(`
          *,
          material_categories (name),
          nearby_companies (name, phone)
        `)
        .order('price', { ascending: true });

      if (error) {
        
        throw error;
      }

      if (!data) {
        
        throw new Error('No data returned from query');
      }

      // Group by category
      const groupedData = data.reduce((acc: Record<string, any[]>, item) => {
        const categoryName = item.material_categories?.name || 'Diğer';
        if (!acc[categoryName]) {
          acc[categoryName] = [];
        }
        acc[categoryName].push({
          ...item,
          supplier_name: item.nearby_companies?.name,
          phone: item.nearby_companies?.phone,
          category_name: categoryName
        });
        return acc;
      }, {});

      setMaterialData(groupedData);
    } catch (err) {
      
      setError(t('marketplace.loading_materials'));
    } finally {
      setLoading(false);
    }
  };

  const handleCall = (phone: string) => {
    const phoneNumber = phone.replace(/\s+/g, '');
    const a = document.createElement('a');
    a.href = `tel:${phoneNumber}`;
    a.setAttribute('target', '_system');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  const isNewPrice = (validFrom: string) => {
    const date = new Date(validFrom);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  const isExpiringSoon = (validUntil: string) => {
    const date = new Date(validUntil);
    const now = new Date();
    const diffTime = Math.abs(date.getTime() - now.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 3;
  };

  // Memoize filtered and sorted data
  const filteredData = useMemo(() => {
    const normalizedSearch = searchQuery.toLowerCase();
    
    return Object.entries(materialData).reduce((acc: Record<string, any[]>, [category, materials]) => {
      if (selectedCategory !== "Hepsi" && category !== selectedCategory) {
        return acc;
      }

      const filteredMaterials = materials.filter(material =>
        material.material_name?.toLowerCase().includes(normalizedSearch) ||
        material.brand?.toLowerCase().includes(normalizedSearch) ||
        material.supplier_name?.toLowerCase().includes(normalizedSearch)
      );

      if (filteredMaterials.length > 0) {
        acc[category] = filteredMaterials;
      }

      return acc;
    }, {});
  }, [materialData, searchQuery, selectedCategory]);

  // Get unique categories for the dropdown
  const categories = useMemo(() => {
    return [t('marketplace.all_categories'), ...Object.keys(materialData)].sort();
  }, [materialData, t]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px] p-4">
        <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-brand-600 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-6">
        <div className="bg-error-50 rounded-full p-5 inline-block mb-4">
          <X className="h-10 w-10 text-error-600" />
        </div>
        <h2 className="text-[18px] font-bold text-gray-900 mb-2">Hata Oluştu</h2>
        <p className="text-[13px] text-gray-500 text-center">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="sticky z-10 bg-white border-b border-gray-200 p-4 space-y-4" style={{
        top: 'var(--safe-area-top, 0px)'
      }}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('marketplace.search_material')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4"
            />
            {searchQuery && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="absolute right-1 top-1 h-8 w-8 p-0" 
                onClick={() => setSearchQuery("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder={t('marketplace.select_category')} />
            </SelectTrigger>
            <SelectContent>
              {categories.map(category => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col space-y-6 p-4">
          {Object.entries(filteredData).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="bg-brand-50 rounded-full p-5 inline-block mb-4">
                <Search className="h-10 w-10 text-brand-400" />
              </div>
              <h3 className="text-[18px] font-bold text-gray-900 mb-2">{t('marketplace.no_results')}</h3>
              <p className="text-[13px] text-gray-500">
                {t('marketplace.material_not_found')}
              </p>
            </div>
          ) : (
            Object.entries(filteredData).map(([category, materials]: [string, any[]]) => {
              // Find the cheapest price in this category
              const cheapestPrice = Math.min(...materials.map(m => m.price));
              
              return (
                <Collapsible key={category}>
                  <CollapsibleTrigger className="w-full">
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-[15px] font-bold text-gray-900">{category}</h3>
                        <span className="text-sm text-gray-500">
                          ({materials.length} {t('marketplace.supplier_count')})
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-[13px] font-semibold text-success-600">
                          {t('marketplace.lowest_price')}: {formatPrice(cheapestPrice)}
                        </span>
                        <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="mt-2 space-y-2">
                      {materials.map((material: any) => (
                        <div
                          key={material.id}
                          className={`p-4 rounded-xl border transition-all hover:shadow-sm ${
                            material.price === cheapestPrice
                              ? 'border-success-200 bg-success-50'
                              : 'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="space-y-1">
                              <div className="flex items-center flex-wrap gap-2">
                                <h4 className="font-medium">{material.supplier_name}</h4>
                                {material.price === cheapestPrice && (
                                  <StatusBadge tone="success">{t('marketplace.cheapest')}</StatusBadge>
                                )}
                                {isNewPrice(material.valid_from) && (
                                  <StatusBadge tone="brand">{t('marketplace.new_price')}</StatusBadge>
                                )}
                                {isExpiringSoon(material.valid_until) && (
                                  <StatusBadge tone="warning">{t('marketplace.expiring_soon')}</StatusBadge>
                                )}
                              </div>
                              <p className="text-sm text-gray-500">
                                {t('marketplace.brand')}: {material.brand}
                              </p>
                              <p className="text-[14px] font-bold text-gray-900">
                                {formatPrice(material.price)}
                              </p>
                              <div className="text-xs text-gray-500">
                                {t('marketplace.validity')}: {formatDate(material.valid_from)} - {formatDate(material.valid_until)}
                              </div>
                            </div>
                            
                            {material.phone && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCall(material.phone)}
                                className="h-8 shrink-0"
                              >
                                <Phone className="h-3 w-3 mr-1.5" />
                                {t('marketplace.call')}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

const PazaryeriPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  return (
    <div className="page-container h-full flex flex-col relative">
      <AppHeader title={t('navigation.marketplace')} />
      
      <main className="main-content flex-1 overflow-hidden">
        <Tabs defaultValue="yakinisletmeler" className="w-full h-full flex flex-col relative">
          <div className="w-full p-3 bg-white border-b border-gray-200 sticky z-40 shadow-xs" style={{
            top: 'var(--header-safe-height)'
          }}>
            <TabsList className="w-full grid grid-cols-2 h-auto bg-gray-100 border border-gray-200 p-1 rounded-md">
              <TabsTrigger value="yakinisletmeler" className="flex items-center justify-center gap-1.5 py-2.5 font-semibold text-sm data-[state=active]:bg-brand-600 data-[state=active]:text-white rounded-md transition-all">
                <Building2 className="h-4 w-4" />
                {t('marketplace.nearby_businesses')}
              </TabsTrigger>
              <TabsTrigger value="malzemeborsasi" className="flex items-center justify-center gap-1.5 py-2.5 font-semibold text-sm data-[state=active]:bg-brand-600 data-[state=active]:text-white rounded-md transition-all">
                <ShoppingCart className="h-4 w-4" />
                {t('marketplace.material_exchange')}
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="h-full flex-1 overflow-hidden">
            <TabsContent value="yakinisletmeler" className="h-full m-0">
              <YakindakiIsletmelerTab navigate={navigate} />
            </TabsContent>
            <TabsContent value="malzemeborsasi" className="h-full m-0">
              <MalzemeBorsasiTab />
            </TabsContent>
          </div>
        </Tabs>
      </main>
      
      {/* Add Company Button - moved to inside the filter area */}
      
      <BottomNavigation />
    </div>
  );
};

export default PazaryeriPage;